import { useEffect, useState } from "react";
import { Star, Trash2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

const Stars = ({ n }) => (
  <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= n ? "text-gold" : "text-purple-200"} fill={i <= n ? "currentColor" : "none"} />)}</span>
);

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (r, patch, msg) => {
    try { await api.patch(`/admin/reviews/${r.review_id}`, patch); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${r.review_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const featured = reviews.filter((r) => r.featured).length;

  return (
    <div data-testid="admin-reviews">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Client <span className="italic text-purple-700">reviews</span></h1>
        <p className="text-sm text-slate-500 mt-2 flex items-center gap-2"><Sparkles size={14} className="text-gold" /> Approve reviews to keep them, feature the best ones to show them on the home page. {featured}/6 featured.</p>
      </FadeUp>
      <div className="mt-10 space-y-4">
        {reviews.map((r) => (
          <FadeUp key={r.review_id} className={`bg-white rounded-2xl border p-6 flex flex-wrap gap-4 items-start ${r.featured ? "border-gold" : "border-purple-100"}`} data-testid={`admin-review-${r.review_id}`}>
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-3">
                <p className="font-medium text-obsidian">{r.client_name}</p>
                <Stars n={r.rating} />
                {r.featured && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/20 text-obsidian">Featured</span>}
                {!r.approved && <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">New</span>}
              </div>
              <p className="text-xs text-slate-400 mt-1">{r.service_name} · {new Date(r.created_at).toLocaleDateString()}</p>
              <p className="text-sm text-slate-700 mt-3 leading-relaxed font-serif italic">"{r.text}"</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!r.approved && (
                <button onClick={() => update(r, { approved: true }, "Approved")} data-testid={`review-approve-${r.review_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700 transition-colors"><Check size={13} /> Approve</button>
              )}
              <button onClick={() => update(r, { featured: !r.featured }, r.featured ? "Removed from home page" : "Featured on home page")} data-testid={`review-feature-${r.review_id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs transition-colors ${r.featured ? "bg-lilac-mist text-obsidian" : "bg-obsidian text-white hover:text-gold"}`}>
                <Sparkles size={13} /> {r.featured ? "Unfeature" : "Feature"}
              </button>
              <button onClick={() => remove(r)} data-testid={`review-delete-${r.review_id}`} className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
            </div>
          </FadeUp>
        ))}
        {reviews.length === 0 && <p className="text-sm text-slate-400 italic">No reviews yet — clients can leave one from any completed session in their portal.</p>}
      </div>
    </div>
  );
}
